/**
 * privateShareMessage.js — Emotional share-message generator.
 *
 * Translates the user's card context (lane, temperature, occasion, relationship)
 * into the right human-feeling message so a share doesn't read like
 *   "Check out this card! https://..."
 * but instead reads like
 *   "Someone left you a MoodCard. Open gently: https://..."
 *   "I couldn't say it normally, so I made this. https://..."
 *
 * Source: the user's spec ("Section 10 — Wording + Message That Accompanies
 * The Link"). Each PRESET is a verbatim template from that spec.
 *
 * Public API:
 *   • buildPrivateShareMessage({ card, mood, shareUrl }) → { text, tone, capsule }
 *   • CAPSULES — array of branded link-capsule labels for UI rendering
 *   • PREVIEW_TITLES / PREVIEW_DESCRIPTIONS — for OG metadata rotation
 */

// All templates are exact copies from the spec. {link} placeholder is replaced
// at send time. Templates are PRECEDED by a friendly line for cohesion.
const PRESETS = {
    default:        { text: "Someone left you a MoodCard.\nOpen gently:\n{link}", tone: "soft" },
    strongDefault:  { text: "Someone made this feeling into a card.\nOpen it here:\n{link}", tone: "soft" },
    intimate:       { text: "This felt like something you should see.\nOpen gently:\n{link}", tone: "intimate" },
    romantic:       { text: "I couldn't say it normally, so I made this.\n{link}", tone: "romantic" },
    soft:           { text: "This is easier to open than explain.\n{link}", tone: "soft" },
    funny:          { text: "This is exactly the energy.\nOpen it before I overthink it:\n{link}", tone: "funny" },
    petty:          { text: "No explanation. Just the mood.\n{link}", tone: "petty" },
    healing:        { text: "A little feeling I needed to release.\n{link}", tone: "healing" },
    grief:          { text: "I left something here.\nRead gently:\n{link}", tone: "grief" },
    celebration:    { text: "This deserved more than a regular post.\n{link}", tone: "celebration" },
    friendship:     { text: "This reminded me of us.\nOpen it:\n{link}", tone: "friendship" },
    faith:          { text: "A quiet word of hope.\nOpen gently:\n{link}", tone: "faith" },
    publicCaption:  { text: "Made a MoodCard for this feeling.\nOpen / remix it:\n{link}", tone: "public" },
    closeFriends:   { text: "This is close-friends energy.\nOpen gently:\n{link}", tone: "soft" },
    minimal:        { text: "open gently:\n{link}", tone: "minimal" },
    mysterious:     { text: "someone left a feeling here.\n{link}", tone: "mystery" },
    viral:          { text: "If this feels like you, remix it:\n{link}", tone: "viral" },
};

// Link-capsule labels for in-app share UI. Rotated stably per card.
export const CAPSULES = [
    "MOODCARD · Open gently",
    "MOODCARD · For your eyes only",
    "MOODCARD · Someone left a feeling",
    "MOODCARD · Tap to reveal",
    "MOODCARD · Private card",
    "MOODCARD · Open this feeling",
];

// Rotating OG preview titles + descriptions for richer link previews on
// WhatsApp / iMessage / Twitter / Telegram.
export const PREVIEW_TITLES = [
    "Someone left you a MoodCard.",
    "Open gently.",
    "For your eyes only.",
    "A feeling is waiting.",
    "Someone made this for a mood.",
    "This was easier than explaining.",
    "Tap to open the feeling.",
    "A private MoodCard is waiting.",
    "Made with MoodCard.",
    "Open this MoodCard.",
];

export const PREVIEW_DESCRIPTIONS = [
    "A quiet feeling is waiting inside.",
    "Open gently. No signup required.",
    "Someone turned a feeling into a card.",
    "Read slowly.",
    "This MoodCard was made to be felt.",
    "Tap to reveal it.",
    "Private emotional card. Open safely.",
    "A feeling was left here for you.",
    "Make your version after opening.",
];

function _stableHash(s) {
    let h = 0;
    if (!s) return 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h);
}

function _pickStable(arr, seed) {
    return arr[_stableHash(seed) % arr.length];
}

/**
 * Decide which preset matches this card's emotional context.
 *
 * Priority order matters. The relationship dimension can override lane (e.g.,
 * a heart card written for "the group chat" should be friendship-toned, not
 * romantic). The lane×temperature combination drives most picks.
 */
function _pickPreset({ lane, temperature, occasion, relationship }) {
    const t = (temperature || "").toLowerCase();
    const rel = (relationship || "").toLowerCase();
    const occ = (occasion || "").toLowerCase();

    // ────────── Relationship-first overrides ──────────
    if (rel === "self" || rel === "past me" || rel === "future me" || rel === "myself, frankly") {
        // Cards written FOR oneself rarely get sent to others — but if they do,
        // the natural framing is healing/release.
        if (lane === "drive") return PRESETS.viral;
        return PRESETS.healing;
    }
    if (rel === "no one in particular" || rel === "no one specifically" || rel === "no one (subtweet)" || rel === "the universe") {
        return PRESETS.mysterious;
    }

    // ────────── Lane × temperature mapping ──────────
    if (lane === "heart") {
        if (rel === "partner" || rel === "crush" || rel === "ex" || rel === "situationship") {
            // Romantic intent. Vulnerable temperatures lean softer.
            if (t === "vulnerable" || t === "soft" || t === "tender") return PRESETS.soft;
            return PRESETS.romantic;
        }
        if (t === "aching" || t === "longing") return PRESETS.grief;
        return PRESETS.intimate;
    }

    if (lane === "healing") return PRESETS.healing;
    if (lane === "faith")   return PRESETS.faith;

    if (lane === "funny" || lane === "banter") {
        if (t === "petty") return PRESETS.petty;
        if (t === "deadpan" || t === "observational" || t === "playful") return PRESETS.funny;
        return PRESETS.funny;  // default chaotic energy
    }

    if (lane === "life") {
        if (t === "celebratory" || t === "warm" || t === "grateful") return PRESETS.celebration;
        if (t === "wistful" || t === "reflective" || (occ && (occ.includes("goodbye") || occ.includes("in memoriam")))) return PRESETS.grief;
        return PRESETS.celebration;
    }

    if (lane === "civic") return PRESETS.publicCaption;

    if (lane === "drive") {
        // Drive cards are confidence/ambition statements. Public + viral.
        if (rel === "the doubters" || rel === "public") return PRESETS.viral;
        return PRESETS.publicCaption;
    }

    // ────────── Pure relationship cues (when lane is missing) ──────────
    if (rel === "the group chat" || rel === "friends" || rel === "best friend") return PRESETS.friendship;
    if (rel === "family" || rel === "community" || rel === "everyone gathered") return PRESETS.celebration;

    // ────────── Final fallback ──────────
    return PRESETS.default;
}

/**
 * Build the private share message for a card.
 *
 * Inputs:
 *   • card  — the chosen card (with .id and .style) for seeding rotation
 *   • mood  — the moodcard root with .personalize (lane/temperature/occasion/relationship)
 *   • shareUrl — the link to substitute into {link}
 *
 * Returns:
 *   { text, tone, capsule }
 *   • text     — final message string with the link substituted
 *   • tone     — short label ("romantic" | "soft" | "funny" | …) for UI badges
 *   • capsule  — the branded "MOODCARD · …" pill label
 */
/**
 * Capitalize first letter of every sentence in a message. Idempotent.
 * Splits on . ! ? + newlines. Preserves trailing punctuation and the URL
 * (URLs are matched anywhere and not touched — the http(s):// scheme is
 * pulled out before transformation so it never becomes 'Https://').
 */
function _properCase(text) {
    if (!text) return text;
    // Reserve URL spans so the sentence-case pass below can't capitalize them.
    const urls = [];
    const placeheld = text.replace(/(https?:\/\/\S+)/gi, (m) => {
        urls.push(m);
        return `\u0000URL${urls.length - 1}\u0000`;
    });
    // Capitalize first non-whitespace char after start, ".", "!", "?", or newline.
    const cased = placeheld.replace(/(^|[.!?]\s+|\n+\s*)([a-z])/g, (_, lead, ch) => lead + ch.toUpperCase());
    return cased.replace(/\u0000URL(\d+)\u0000/g, (_, i) => urls[Number(i)] || "");
}

export function buildPrivateShareMessage({ card, mood, shareUrl }) {
    const p = mood?.personalize || {};
    const ctx = {
        lane: p.lane || mood?.lane || null,
        temperature: p.temperature || mood?.temperature || null,
        occasion: p.occasion || null,
        relationship: p.relationship || null,
    };
    const preset = _pickPreset(ctx);
    const url = shareUrl || "";
    // Proper-case sentence starts so SMS / WhatsApp previews read cleanly
    // ("Open gently:" not "open gently:"). The "minimal" preset is the only
    // exception — its lowercase rendering is intentional.
    const rawText = preset.text.replace("{link}", url);
    const text = preset.tone === "minimal" ? rawText : _properCase(rawText);
    const seed = `${card?.id || ""}-${card?.style || ""}-${ctx.lane || ""}`;
    const capsule = _pickStable(CAPSULES, seed);
    return { text, tone: preset.tone, capsule };
}

/**
 * Stable rotation helpers for OG metadata. Used server-side at /api/og too,
 * but exposed here for any frontend preview UI.
 */
export function pickPreviewTitle(seed) {
    return _pickStable(PREVIEW_TITLES, seed || "default");
}
export function pickPreviewDescription(seed) {
    return _pickStable(PREVIEW_DESCRIPTIONS, seed || "default");
}
